import { Button } from "@/components/generic/Button";
import { Modal } from "@/components/generic/Modal";
import { CircleQuestionMarkIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

interface AlertModalProps {
  title: string;
  description: string | React.ReactNode;
  icon?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  disableConfirmButton?: boolean;
  iconDisable?: boolean;
  dismissShouldNotReset?: boolean;
  size?: "lg" | "sm" | "md" | "xl" | "2xl" | "full";
  className?: string;
  buttonChildren?: React.ReactNode;
  confirmation?: React.ReactNode;
  buttonsContainerClassName?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  /**
   * Called when the dialog closes, regardless of whether the user confirmed, cancelled, or dismissed the dialog.
   * Useful for resetting state when the dialog is closed.
   */
  onClose?: () => void;
  /**
   * Called when the user clicks the cancel button. Note that this is not called when the user dismisses
   * the dialog by clicking outside of it or pressing escape, in those cases onClose is called but not onCancel.
   */
  onCancel?: () => void;
  /**
   * Called when the user clicks the confirm button. Note that this is not called when the user dismisses
   * the dialog by clicking outside of it or pressing escape, in those cases onClose is called but not onConfirm.
   */
  onConfirm?: () => void;
}

export const AlertModal = ({
  className,
  title,
  description,
  icon,
  isOpen = true,
  onOpenChange,
  disableConfirmButton = false,
  iconDisable = false,
  size = "md",
  buttonChildren,
  confirmation,
  buttonsContainerClassName,
  cancelButtonText = "Fortryd",
  confirmButtonText = "Bekræft",
  onClose,
  onCancel,
  onConfirm,
}: AlertModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        onOpenChange?.(isOpen);
        if (!isOpen) onClose?.();
      }}
      isDismissable={true}
      size={size ?? "md"}
      intent="default"
      animation="fade"
      className={twMerge("text-theme-on-glass flex justify-center", className)}
      icon={
        icon ||
        (!iconDisable ? (
          <CircleQuestionMarkIcon className="text-theme-active" />
        ) : (
          false
        ))
      }
    >
      {({ close }) => (
        <>
          <div className="mt-4 flex max-w-104.75 flex-col items-center gap-4">
            <span className="text-md font-medium">
              {title}
            </span>

            <span className="text-theme-on-glass-variant text-center text-sm">
              {description}
            </span>
          </div>

          <div
            className={twMerge(
              buttonsContainerClassName,
              "flex justify-center gap-6 pt-10",
            )}
          >
            {buttonChildren}
            {!buttonChildren && (
              <>
                <Button
                  animate
                  intent="secondary"
                  onPress={() => {
                    onCancel?.();
                    close();
                  }}
                >
                  {cancelButtonText}
                </Button>

                <Button
                  isDisabled={disableConfirmButton}
                  animate
                  onPress={() => {
                    onConfirm?.();
                    close();
                  }}
                >
                  {confirmButtonText}
                </Button>
              </>
            )}
          </div>

          {confirmation && <div className="pt-5">{confirmation}</div>}
        </>
      )}
    </Modal>
  );
};
